import { ThirdPartyCost } from '@app/core/model/third-party-cost.model'
import { DeliverableReviewer } from '@app/features/scope-overview/model/deliverable-reviewer.interface'
import { DeliverableSection } from '@app/features/scope-overview/model/deliverable-section'
import { DeliverableType } from '@app/features/scope-overview/model/deliverable-type'
import { FeeItemInstance } from '@app/features/scope-overview/model/fee-item.model'
import { Money } from '@app/features/scope-overview/model/money.model'
import { RatecardVersion } from '@app/features/scope-overview/model/ratecard-version.model'
import { ScopeSection } from '@app/features/scope-overview/model/scope-section'
import { ScopeComponent } from '@app/features/scoping/models/component.model'
import { Role } from '@app/features/scoping/models/role.model'
import { UserComment } from '@core/model/comment.model'
import { Company } from '@core/model/company.model'
import { User } from '@core/model/user.model'
import { formatHours } from '@shared/utils/utils'
import { Type } from 'class-transformer'
import { isNil } from 'lodash'
import { ChangeAuditData } from './change-audit-data.model'
import { getTotalFees } from '@app/shared/components/scope-elements/utils'

export enum DeliveryStatus {
  COMPLETE = 'COMPLETE',
  ON_HOLD = 'ON_HOLD',
  IN_PROGRESS = 'IN_PROGRESS',
  TRADED = 'TRADED',
  TRADING_FROM_IN_PROGRESS = 'TRADING_FROM_IN_PROGRESS',
  TRADING_TO_IN_PROGRESS = 'TRADING_TO_IN_PROGRESS',
}

export interface SelectedDeliverable {
  name?: string
  deliverableType?: DeliverableType
  budget?: Money
  startDate?: Date
  endDate?: Date
  description?: string
  trafficSystemEntityMetadata?: any
}

export interface TradedDeliverable {
  sourceType: string
  id: number
  rateCardUnitMinutes: number
  rateCardTotalMinutes: number
  totalCostPrice: Money
  unitCostPrice: Money
  markup: number
  totalProfit: Money
  unitProfit: Money
}

export class DeliverableCalculatedValues {
  ftePercentage: number
  markup: number
  rateCardTotalMinutes: number
  ratecardTotalMinutes?: number
  totalCostPrice: number
  totalProfit: number
  totalSellingPrice: number
  naturalSellingPrice?: number
  unitCostPrice: number
  unitSellingPrice: number
  @Type(() => Date) updatedOn: Date
}

export class Deliverable {
  @Type(() => Money) budgetBalance!: Money
  @Type(() => Money) budget?: Money
  @Type(() => Money) thirdPartyCostBudget?: Money
  cachedScopeMarkTotalHours!: number
  cachedScopeMarkTotalSellingPrice: any
  @Type(() => UserComment) comments!: UserComment[]
  @Type(() => Company)
  company!: Company
  componentsInitialised?: boolean = false
  containsUnmappedScopeMarkRoles!: boolean
  @Type(() => User)
  createdBy!: User
  @Type(() => Money) fixedCost!: Money
  @Type(() => Money) newFixedCostValue!: Money
  @Type(() => Date) createdTs!: Date
  @Type(() => ScopeSection) section!: ScopeSection
  @Type(() => ScopeComponent) components!: ScopeComponent[]
  deliveryStatus!: DeliveryStatus
  tradedFrom?: any
  currencyUnit!: string
  current!: boolean
  deleted!: boolean
  deliverableStatusTrading!: boolean
  deliverableTimeSheetEntries!: any[]
  description!: string
  extraDescription!: string
  internalNote!: string
  discipline!: any
  @Type(() => Date) endDate!: Date
  @Type(() => FeeItemInstance) feeItems: FeeItemInstance[] = []
  fixedPricing!: boolean
  fixedCostEditable!: boolean
  id!: number
  identificationType!: string
  language!: string
  librarySourced!: boolean
  markup!: number
  name!: string
  naturalSellingPrice!: any
  order!: number
  @Type(() => User)
  owner!: User
  privilegeCategories!: string[]
  quantity!: number
  rateCardTotalHours!: number
  rateCardTotalMinutes!: number
  rateCardUnitMinutes!: number
  readyToTraffic!: boolean
  reviewers!: DeliverableReviewer[]
  scope!: any
  scopeMarkTotalHours!: number
  @Type(() => Money) scopeMarkTotalSellingPrice!: Money
  scopeSourced!: boolean
  @Type(() => DeliverableSection) sections!: DeliverableSection[]
  source!: any
  sourceType!: string
  @Type(() => Date) startDate!: Date
  @Type(() => ThirdPartyCost) thirdPartyCosts: ThirdPartyCost[] = []
  @Type(() => Money) totalCostPrice!: Money
  @Type(() => Money) totalProfit!: Money
  @Type(() => Money) totalSellingPrice!: Money
  @Type(() => Money) unitSellingPrice!: Money
  totalRateCardHours!: string
  traded!: boolean
  tradedTo!: boolean
  trafficSystemEntityMetadata!: any
  type!: any
  unitCostPrice!: any
  unitProfit!: any
  @Type(() => User)
  updatedBy!: User
  @Type(() => Date) updatedTs!: Date
  @Type(() => Money) fixedCostOffset!: Money
  userAdditionalPrivileges!: any[]
  userPrivilegeRestrictions!: any[]
  tradesCount: number
  traffickedActualMinutes: number
  traffickedPercentageComplete: number
  feeValueTotal!: number
  calculatedValues?: DeliverableCalculatedValues
  @Type(() => Money) tpcValueTotal!: Money
  changeAuditData?: ChangeAuditData
  // CR properties
  isRemoved?: boolean
  isAdded?: boolean
  hasChanges?: boolean

  isTraded() {
    return this.deliveryStatus === DeliveryStatus.TRADED
  }

  isBeingTraded?() {
    return this.deliveryStatus === DeliveryStatus.TRADING_FROM_IN_PROGRESS
  }

  isPendingTrade?() {
    return this.deliveryStatus === DeliveryStatus.TRADING_TO_IN_PROGRESS
  }

  isTradeProcessing() {
    return this.isPendingTrade() || this.isBeingTraded()
  }

  getTotalProfit() {
    if (this.calculatedValues) return new Money(this.calculatedValues.totalProfit, this.currencyUnit)

    if (!this.componentsInitialised || !this.components?.length) return this.totalProfit

    return this.getUnitProfit().multiply(this.quantity)
  }

  getTotalSellingPrice?(ctx: any = {}) {
    if (this.calculatedValues) return new Money(this.calculatedValues?.totalSellingPrice, this.currencyUnit)

    if (!this.componentsInitialised || !this.components?.length)
      return this.totalSellingPrice || new Money(null, this.currencyUnit)

    if (this.getUnitSellingPrice(ctx)) {
      if (ctx) ctx.applyQuantity = true
      else ctx = { excludeFixedCost: false, excludeThirdPartyCosts: false, excludeFees: false, applyQuantity: true }

      return this.getUnitSellingPrice(ctx)
    }
    return new Money(null, this.currencyUnit)
  }

  getUnitNaturalSellingPrice?() {
    if (!this.calculatedValues) return this.getUnitSellingPrice({ excludeFixedCost: true })

    const unitSellingPrice = this.getUnitSellingPrice()

    if (isNil(unitSellingPrice)) return null

    if (!isNil(this.fixedCostOffset)) return unitSellingPrice.subtractValue(this.fixedCostOffset?.amount || 0)
    return new Money(
      this.calculatedValues.naturalSellingPrice != null
        ? this.calculatedValues.naturalSellingPrice / (this.quantity || 1)
        : unitSellingPrice.amount,
      this.currencyUnit
    )
  }

  getFeePercentageBase(): number {
    if (!this.calculatedValues) return 0
    const base = this.calculatedValues.naturalSellingPrice || 0
    const offset = this.fixedCostOffset?.amount
    return offset != null ? base - offset * (this.quantity || 1) : base
  }

  getNaturalTotalSellingPrice(): Money | null {
    const unitNatural = this.getUnitNaturalSellingPrice()
    if (!unitNatural) return null
    return new Money(unitNatural.amount * (this.quantity || 1) + this.getTotalFees(), this.currencyUnit)
  }

  getUnitSellingPrice(
    ctx: any = { excludeFixedCost: false, excludeThirdPartyCosts: false, excludeFees: false, applyQuantity: false }
  ) {
    if (this.calculatedValues) {
      let unitSellingPrice = new Money(this.calculatedValues?.unitSellingPrice || 0, this.currencyUnit)

      if (ctx.excludeFixedCost) unitSellingPrice = unitSellingPrice.subtractValue(this.fixedCostOffset?.amount || 0)

      return unitSellingPrice
    }

    if (!this.componentsInitialised || !this.components?.length) return this.unitSellingPrice

    const accu = () => {
      let total = new Money(0, this.currencyUnit)

      if (this.sections) {
        for (const section of this.sections) {
          const totalUserSellingPrice = section.getTotalSellingPrice(ctx, this)
          if (totalUserSellingPrice == null) return null
          total = total.add(totalUserSellingPrice)
        }
      }

      const defaultSectionComponents = this.defaultSectionComponents
      if (defaultSectionComponents) {
        for (const component of defaultSectionComponents) {
          const totalUserSellingPrice = component.getTotalSellingPrice(ctx)
          if (totalUserSellingPrice == null) return null
          total = total.add(totalUserSellingPrice)
        }
      }

      if (!ctx.excludeThirdPartyCosts)
        this.thirdPartyCosts?.forEach(function (tpc: any) {
          total = total.add(tpc.calculateSellingPrice())
        })

      return total
    }

    let total = accu()
    if (total) {
      if (!isNil(this.fixedCostOffset) && !ctx.excludeFixedCost) total = total.add(this.fixedCostOffset)

      if (total) {
        if (ctx.applyQuantity) total = total.multiply(this.quantity)

        if (!ctx.excludeFees && isNil(this.fixedCostOffset)) total = total.addValue(this.getTotalFees())
      }
    }

    return total
  }

  getTotalFees = () => getTotalFees(this)

  getUnitCostPrice() {
    if (this.calculatedValues) return this.calculatedValues.unitCostPrice

    if (!this.componentsInitialised || !this.components?.length) return this.unitCostPrice

    let total = new Money(0, this.currencyUnit)
    for (let i = 0; i < this.components.length; i++) {
      const component = this.components[i]
      const totalCost = component.getTotalCostPrice()
      if (totalCost == null) return null
      total = total.add(totalCost)
    }

    this.thirdPartyCosts?.forEach(function (tpc: any) {
      total = total.add(tpc.calculateCostPrice())
    })

    return total
  }

  getTotalCostPrice?() {
    if (this.calculatedValues) return new Money(this.calculatedValues.totalCostPrice, this.currencyUnit)

    if (!this.componentsInitialised || !this.components?.length) return this.totalCostPrice

    const costPrice = this.getUnitCostPrice()
    if (costPrice != null) return costPrice.multiply(this.quantity)

    return Money.of(0, this.currencyUnit)
  }

  getUnitProfit?() {
    if (this.calculatedValues) {
      const unitProfit = this.calculatedValues.totalProfit / (this.quantity || 1)
      return new Money(unitProfit, this.currencyUnit)
    }

    if (!this.componentsInitialised || !this.components?.length) return this.unitProfit

    const totalUserSellingPrice = this.getUnitSellingPrice({ excludeFixedCost: false })
    if (totalUserSellingPrice != null) {
      const costPrice = this.getUnitCostPrice()
      if (costPrice != null) return totalUserSellingPrice.subtractValue(costPrice.amount)
    }

    return Money.of(0, this.currencyUnit)
  }

  getMarkup?() {
    if (this.calculatedValues) return this.calculatedValues.markup

    if (!this.componentsInitialised) return this.markup

    const sellingPrice = this.getTotalSellingPrice()
    const totalRateCardCost = this.getTotalCostPrice()
    if (isNil(totalRateCardCost)) return null

    const cost = totalRateCardCost
    if (cost.amount == 0) return 100

    if (sellingPrice && (cost.amount || 0) > 0) {
      const grossProfit = sellingPrice.amount - (cost.amount || 0)
      const markup = (grossProfit / (cost.amount || 0)) * 100
      return parseFloat(markup.toFixed(2))
    }

    return 0
  }

  getMargin() {
    return this.getUnitProfit().amount
      ? ((this.getUnitProfit().amount / this.getUnitSellingPrice().amount) * 100).toFixed(2)
      : 0
  }

  private getTotalHours(value: number, includeQuantity = true): number {
    return value * (includeQuantity ? this.quantity : 1)
  }

  getTotalRateCardHours?(includeQuantity = true) {
    if (this.calculatedValues) {
      const totalMinutes = this.calculatedValues?.rateCardTotalMinutes || this.calculatedValues?.ratecardTotalMinutes
      return totalMinutes ? totalMinutes / 60 : 0
    }

    if (!this.componentsInitialised || !this.components?.length)
      return this.getTotalHours(this.rateCardTotalMinutes / 60, includeQuantity)

    let total = 0
    for (let i = 0; i < this.components.length; i++) {
      const hours = this.components[i].getTotalRateCardHours()
      if (hours != null) total += hours
    }

    return this.getTotalHours(total, includeQuantity)
  }

  getBreadcrumb?() {
    return this.section?.name
  }

  getAllComponents?() {
    let components: ScopeComponent[] = []

    this.sections?.forEach((section) => {
      if (section.components?.length > 0) {
        components = components.concat(section.components)
      }
    })

    if (this.components?.length > 0) {
      components = components.concat(this.components)
    }

    return components
  }

  totalScopeMarkSellingPrice(companyCurrency?: string) {
    let currency = companyCurrency
    if (currency == null) {
      currency = this.createdBy?.company?.currencyUnit || null
    }

    const accu = () => {
      let total = new Money(null, currency)

      for (let i = 0; i < this.sections?.length; i++) {
        const section = this.sections[i]
        const totalScopeMarkSellingPrice = section.totalScopeMarkSellingPrice(this)
        if (totalScopeMarkSellingPrice == null || isNaN(totalScopeMarkSellingPrice.getValue())) return null
        total = total.add(totalScopeMarkSellingPrice)
      }

      const defaultSectionComponents = this.defaultSectionComponents
      for (let i = 0; i < defaultSectionComponents?.length; i++) {
        const component = defaultSectionComponents[i]
        if (component.totalScopeMarkSellingPrice() == null) return null
        total = total.add(component.totalScopeMarkSellingPrice())
      }

      return total
    }

    return accu()
  }

  get defaultSectionComponents() {
    return this.components?.filter((c) => !c.section)
  }

  getFeeValueTotal() {
    if (!this.componentsInitialised) return this.feeValueTotal

    let total = this.getTotalFees()
    this.components.forEach((component) => {
      const componentTotal = component.getTotalFees()
      total += componentTotal || 0
    })

    return total
  }

  getTpcValueTotal() {
    if (!this.componentsInitialised) return this.tpcValueTotal

    let total = new Money(0, this.currencyUnit)
    this.thirdPartyCosts?.forEach(function (tpc: any) {
      total = total.add(tpc.calculateCostPrice())
    })
    this.sections.forEach((section) => {
      const sectionTotal = section.getTpcValueTotal()
      total = total.add(sectionTotal)
    })
    this.components.forEach((component) => {
      const componentTotal = component.thirdPartyCostTotal()
      total = total.add(componentTotal)
    })

    return total
  }

  getFtePercentage(fteHours, includeQuantity = true) {
    return this.getTotalRateCardHours(includeQuantity) === 0
      ? 0
      : parseFloat(((this.getTotalRateCardHours(includeQuantity) / fteHours) * 100).toFixed(2))
  }

  getCommentsCount() {
    const ownCommentsOwn = this.comments.filter((c) => c.sourceAttribute).length

    let sectionsCommentsCount = 0
    for (let i = 0; i < this.sections.length; i++) {
      sectionsCommentsCount += this.sections[i].comments.length
    }

    let componentsCommentsCount = 0
    const comps = this.getAllComponents()
    for (let i = 0; i < comps.length; i++) {
      componentsCommentsCount += comps[i].getCommentsCount()
    }

    let tpcCommentsCount = 0
    const thirdPartyCosts = this.thirdPartyCosts
    for (let j = 0; j < thirdPartyCosts.length; j++) {
      tpcCommentsCount += thirdPartyCosts[j].getCommentsCount()
    }

    return ownCommentsOwn + sectionsCommentsCount + componentsCommentsCount + tpcCommentsCount
  }

  getActualHours(): number {
    return this.deliveryStatus !== 'TRADED' && this.deliveryStatus !== 'TRADING_FROM_IN_PROGRESS'
      ? this.traffickedActualMinutes / 60 || 0
      : 0
  }

  recalculateFixedCostOffset(amount: number, returnValue = false) {
    let value = amount || this.getUnitNaturalSellingPrice()?.amount
    if (value < 0) value = 0

    if (isNil(value)) {
      if (!isNil(amount)) {
        value = amount
      } else {
        if (!returnValue) this.fixedCostOffset = null
        return null
      }
    }

    if (value < 0) return null

    const unitSellingPrice = this.getUnitSellingPrice()?.amount
    const unitNaturalSellingPrice = this.getUnitNaturalSellingPrice()?.amount
    const totalValInclusive = unitSellingPrice ? unitSellingPrice || 0 : 0
    const totalValExclusive = unitSellingPrice ? (unitNaturalSellingPrice ?? totalValInclusive) : 0

    if (value !== totalValInclusive) {
      const fixedCostOffset = new Money(
        +parseFloat((value - totalValExclusive).toString()).toFixed(2),
        this.currencyUnit
      )

      if (returnValue) return fixedCostOffset
      this.fixedCostOffset = fixedCostOffset
    }

    return this.fixedCostOffset
  }
}

export class LibraryTaskTemplate extends Deliverable {
  @Type(() => RatecardVersion) rateCardVersion!: RatecardVersion
  @Type(() => Role) roles!: Role[]

  override getTotalSellingPrice(_ctx: any = {}): Money {
    return this.roles.reduce(
      (total, role) => {
        if (!role.rate || !role.rate.amount) return total
        let userPrice = role.rate
        if (role.ratecardLocationRole) {
          userPrice = Money.of(role.ratecardLocationRole?.rateAmount || 0, role.rate.currency)
        }
        const roundedHours = (Math.round(role.minutes / 15) * 15) / 60
        const rolePrice = userPrice.multiply(roundedHours)
        return total.add(rolePrice)
      },
      Money.of(0, this.rateCardVersion.currencyCode)
    )
  }

  override getTotalCostPrice(): Money {
    return this.roles.reduce(
      (total, role) => {
        if (!role.cost || !role.cost.amount) return total
        let userPrice = role.cost
        if (role.ratecardLocationRole) {
          userPrice = Money.of(role.ratecardLocationRole?.costAmount || 0, role.cost.currency)
        }
        const roundedHours = (Math.round(role.minutes / 15) * 15) / 60
        const roleCost = userPrice.multiply(roundedHours)
        return total.add(roleCost)
      },
      Money.of(0, this.rateCardVersion.currencyCode)
    )
  }

  override getTotalRateCardHours(_includeQuantity = true): number {
    return this.roles.reduce((total, role) => {
      if (!role.minutes) return total
      const roundedHours = (Math.round(role.minutes / 15) * 15) / 60
      return total + roundedHours
    }, 0)
  }

  getFormattedHours(): string {
    return formatHours(this.getTotalRateCardHours())
  }
}
